import styles from './EntityPickerActions.module.scss';

import { Theme, Typography, useMediaQuery } from '@mui/material';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import {
  IUserGroupPickerProps,
  UserGroupPicker,
} from '@work4all/components/lib/components/entity-picker/user-group-picker/UserGroupPicker';
import { ChipList } from '@work4all/components/lib/dataDisplay/chip/ChipList';

import {
  Chip,
  PickerTargetButton,
} from '../../containers/mask-overlays/locked-inputs';

interface UserGroupPickerFieldProps<TMultiple extends boolean>
  extends IUserGroupPickerProps<TMultiple> {
  label?: string;
  error?: string;
  disabled?: boolean;
  fieldComp?: React.ReactNode;
}

const UserGroupPickerField = function UserGroupPickerField<
  TMultiple extends boolean
>(
  props: UserGroupPickerFieldProps<TMultiple>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    error,
    disabled,
    label,
    multiple = false,
    favorites = true,
    fieldComp,
    ...pickerProps
  } = props;
  const { value, onChange, clearable = true } = pickerProps;

  const { t } = useTranslation();

  let labelValue: string | JSX.Element = '';

  if (multiple && Array.isArray(value)) {
    labelValue = (
      <ChipList>
        {value.map((x) => {
          return (
            <Chip
              key={x.id}
              maxWidth={18}
              label={x.name}
              handleDelete={() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange(value.filter((y) => y.id !== x.id) as any);
              }}
            />
          );
        })}
      </ChipList>
    );
  } else if (!Array.isArray(value) && value?.id) {
    labelValue = (
      <div className={styles.userItem}>
        <Typography
          variant="body1"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value.name}
        </Typography>
      </div>
    );
  }

  const field = (
    <PickerTargetButton
      ref={ref}
      value={labelValue}
      label={label ?? t('COMMON.USERGROUP')}
      error={error}
      multiline={multiple}
      disabled={disabled}
      onClear={
        disabled || !clearable
          ? undefined
          : () => {
              onChange(null);
            }
      }
    />
  );

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  if (disabled) {
    return field;
  }

  return (
    <EntityPickerPopover
      picker={
        <UserGroupPicker
          favorites={favorites}
          placeholder={t('SEARCH.USER_GROUP_SEARCH_BY')}
          {...pickerProps}
          multiple={multiple}
        />
      }
      fullscreen={isMobile}
    >
      {fieldComp ? <div>{fieldComp}</div> : field}
    </EntityPickerPopover>
  );
};

type UserPickerFieldPropsWithOptionalMultiple<T extends boolean> =
  T extends true
    ? UserGroupPickerFieldProps<T>
    : Omit<UserGroupPickerFieldProps<T>, 'multiple'> &
        Partial<Pick<UserGroupPickerFieldProps<T>, 'multiple'>>;

const UserPickerFieldWithRef = forwardRef(UserGroupPickerField) as <
  TMultiple extends boolean = false
>(
  props: UserPickerFieldPropsWithOptionalMultiple<TMultiple> &
    React.RefAttributes<HTMLDivElement>
) => JSX.Element;

export { UserPickerFieldWithRef as UserGroupPickerField };
